import { useCallback, useMemo, useState } from "react";
import { AssetCategory, PairedVelavuDevice } from "velavu-js-api";
import { getLocationFloorID } from "./floor-helper";
import { compoundSearchAssets } from "./search-helper";

export interface AssetFilteringState<T> {
	devices: T;

	searchText: string;
	showFilterMenu: boolean;
	filterCategory: AssetCategory | undefined;
	filterStatus: boolean | undefined;
	isFiltering: boolean;

	setSearchText: (text: string) => void;
	toggleFilterMenu: () => void;
	setFilterCategory: (category: AssetCategory | undefined) => void;
	setFilterStatus: (status: boolean | undefined) => void;
}

export default function useAssetFiltering<T extends PairedVelavuDevice>(
	devices: T[],
	filterFloor?: string,
): AssetFilteringState<T[]>;
export default function useAssetFiltering<T extends PairedVelavuDevice>(
	devices: T[] | undefined,
	filterFloor?: string,
): AssetFilteringState<T[] | undefined>;
export default function useAssetFiltering<T extends PairedVelavuDevice>(
	devices: T[] | undefined,
	filterFloor?: string,
): AssetFilteringState<T[] | undefined> {
	const [searchText, setSearchText] = useState("");

	const [showFilterMenu, setShowFilterMenu] = useState(false);
	const toggleFilterMenu = useCallback(
		() => setShowFilterMenu((it) => !it),
		[setShowFilterMenu],
	);
	const [filterCategory, setFilterCategory] = useState<
		AssetCategory | undefined
	>(undefined);
	const [filterStatus, setFilterStatus] = useState<boolean | undefined>(
		undefined,
	);

	const filteredDevices = useMemo(() => {
		if (devices === undefined) {
			return undefined;
		}

		let copy = [...devices];

		//Filtering the assets by category
		if (filterCategory !== undefined) {
			copy = copy.filter(
				(device) => device.asset.category === filterCategory,
			);
		}

		//Filtering the assets by status
		if (filterStatus !== undefined) {
			copy = copy.filter((device) => device.online === filterStatus);
		}

		//Filtering the assets by search query
		const searchItems = copy.map((device) => ({
			name: device.asset.name,
			category: device.asset.category,
			group: device.asset.group,
			device: device,
		}));
		copy = compoundSearchAssets(searchItems, searchText).map(
			(it) => it.device,
		);

		//Filtering the assets by floor
		if (filterFloor !== undefined) {
			copy = copy.filter((device) => {
				const assetFloorID = getLocationFloorID(device?.location);
				return (
					assetFloorID === undefined || assetFloorID === filterFloor
				);
			});
		}

		return copy;
	}, [devices, filterCategory, filterStatus, searchText, filterFloor]);

	return useMemo(
		() => ({
			devices: filteredDevices,

			searchText,
			showFilterMenu,
			filterCategory,
			filterStatus,
			isFiltering:
				filterCategory !== undefined || filterStatus !== undefined,

			setSearchText,
			toggleFilterMenu,
			setFilterCategory,
			setFilterStatus,
		}),
		[
			filteredDevices,

			searchText,
			showFilterMenu,
			filterCategory,
			filterStatus,

			setSearchText,
			toggleFilterMenu,
			setFilterCategory,
			setFilterStatus,
		],
	);
}
